import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import type { UseFormProps } from 'react-hook-form';
import { z } from 'zod';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);

export const TARGET_TYPES = ['user', 'ip', 'host'] as const;

const HOST_TARGET_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;
const USERNAME_TARGET_REGEX = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_.@/\\+-]+$/;
const INVESTIGATION_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_ ]*$/;

const UserTarget = z.object({
  type: z.literal('user'),
  value: z
    .string()
    .trim()
    .min(1, 'Username is required')
    .regex(USERNAME_TARGET_REGEX, {
      message: String.raw`Invalid Username format. Allowed characters are letters, numbers, and special characters: . _ @ / \+ -`,
    }),
});

const HostTarget = z.object({
  type: z.literal('host'),
  value: z.string().trim().min(1, 'Host is required').regex(HOST_TARGET_REGEX, {
    message:
      'Invalid hostname format. Hostname must start with a letter or number and can include letters, numbers, hyphens, and underscores.',
  }),
});

const IpTarget = z.object({
  type: z.literal('ip'),
  value: z.ipv4({ message: 'Invalid IP Address format (e.g., 192.168.1.1)' }),
});

const DateRangeSchema = z
  .object({
    from: z.date(),
    to: z.date(),
  })
  .optional()
  .refine((r) => !!r?.from && !!r?.to, {
    message: 'Please select both start and end dates',
  })
  .refine(
    (r) => {
      if (!r?.to) return true;
      const endTime = dayjs.utc(r.to).startOf('minute');
      const nowUtc = dayjs.utc().startOf('minute');

      // we account for a few minutes of latency,
      return endTime.isSameOrBefore(nowUtc.add(2, 'minute'));
    },
    {
      message: 'Investigation end time cannot be in the future (UTC)',
    },
  )
  .refine(
    (r) => {
      if (!r?.from || !r?.to) return true;
      const maxMs = 14 * 24 * 60 * 60 * 1000;
      const diff = dayjs.utc(r.to).diff(dayjs.utc(r.from));
      return diff <= maxMs;
    },
    {
      message:
        'Investigations cannot be longer than 14 days. If you need to cover a larger interval, please create multiple investigations.',
    },
  );

const TargetSchema = z.discriminatedUnion('type', [UserTarget, HostTarget, IpTarget]);

export const InvestigationFormSchema = z.object({
  traceName: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(255, 'Name cannot exceed 255 characters')
    .regex(INVESTIGATION_NAME_REGEX, {
      message: 'Name must start with a letter or digit and can contain spaces or underscores',
    }),
  dateRange: DateRangeSchema,
  assets: z.array(z.string()).min(1, 'At least one application instance is required'),
  targets: z.array(TargetSchema).min(1, 'At least one scope condition is required'),
  sendEmailNotification: z.boolean().optional(),
  reservationId: z.string(),
});

export type NewInvestigationFormValues = z.infer<typeof InvestigationFormSchema>;

export const getNewInvestigationFormOptions = (): UseFormProps<NewInvestigationFormValues> => ({
  resolver: zodResolver(InvestigationFormSchema),
  defaultValues: {
    traceName: '',
    dateRange: undefined,
    assets: [],
    targets: [{ type: 'user', value: '' }],
    sendEmailNotification: true,
    reservationId: '',
  },
  mode: 'onSubmit',
  criteriaMode: 'all',
});
